import type {
  DashboardProjectBreakdownClassificationType,
  DashboardProjectBreakdownType,
} from "@/features/Dashboard/types/DashboardType";

export type BreakdownObjectClass = { code: string; name: string };

export const UNASSIGNED_OBJECT_CLASS_KEY = "__noObjectClass__";
export const UNASSIGNED_OBJECT_CLASS_LABEL = "No object class";

type ClassificationNode = DashboardProjectBreakdownClassificationType[string];

// Classification codes sort naturally, not lexically ("21.2" before "21.10").
const sortByCode = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

/**
 * The breakdown codes come from the dashboard API while the lookup keys come from the classification
 * code table; trim both sides so stray whitespace in either source can't break the match.
 */
export const normalizeBreakdownCode = (code: string | null | undefined): string => (code ?? "").trim();

/**
 * Inserts the classification object class as the outermost level of the project breakdown.
 *
 * The breakdown tree is built server-side and arrives with classification codes at the top. Systems
 * that have an object class level (CCS, CCI) group those codes one level higher; systems without one
 * are returned untouched. The synthetic group nodes have the same shape as real classification
 * nodes, so the recursive Classification component renders them without any change.
 *
 * The input is never mutated — the raw breakdown stays exposed alongside this derived view.
 *
 * @param breakdown - The breakdown exactly as the dashboard API returned it
 * @param objectClassByCode - Classification code → its object class, keyed with `normalizeBreakdownCode`
 */
export function groupBreakdownByObjectClass(
  breakdown: DashboardProjectBreakdownType,
  objectClassByCode: Map<string, BreakdownObjectClass>
): DashboardProjectBreakdownType {
  const classifications = breakdown?.classifications;
  if (!classifications || objectClassByCode.size === 0) return breakdown;

  const entries = Object.entries(classifications);
  if (entries.length === 0) return breakdown;

  // Nothing resolvable means either a system without object classes or a code table that hasn't
  // loaded yet — either way the original tree is the right thing to render.
  const hasResolvable = entries.some(([, node]) => objectClassByCode.has(normalizeBreakdownCode(node.classification?.code)));
  if (!hasResolvable) return breakdown;

  const groups = new Map<string, { objectClass: BreakdownObjectClass; children: [string, ClassificationNode][] }>();

  for (const entry of entries) {
    const [, node] = entry;
    const objectClass = objectClassByCode.get(normalizeBreakdownCode(node.classification?.code));
    const groupKey = objectClass?.code ?? UNASSIGNED_OBJECT_CLASS_KEY;
    const group = groups.get(groupKey);
    if (group) {
      group.children.push(entry);
    } else {
      groups.set(groupKey, {
        objectClass: objectClass ?? { code: "", name: UNASSIGNED_OBJECT_CLASS_LABEL },
        children: [entry],
      });
    }
  }

  // Object classes in code order, with the unassigned bucket always last.
  const orderedKeys = [...groups.keys()].filter((key) => key !== UNASSIGNED_OBJECT_CLASS_KEY).sort(sortByCode);
  if (groups.has(UNASSIGNED_OBJECT_CLASS_KEY)) orderedKeys.push(UNASSIGNED_OBJECT_CLASS_KEY);

  const grouped: DashboardProjectBreakdownClassificationType = {};
  for (const groupKey of orderedKeys) {
    // safe: every key comes from groups.keys()
    const group = groups.get(groupKey)!;
    grouped[groupKey] = {
      classification: { code: group.objectClass.code, name: group.objectClass.name },
      // The API only totals per classification code, so the group totals are summed here. Group rows
      // have no types of their own — every type hangs off a classification code below them.
      elements: group.children.reduce((total, [, node]) => total + (node.elements ?? 0), 0),
      emission: group.children.reduce((total, [, node]) => total + (node.emission ?? 0), 0),
      classifications: Object.fromEntries(group.children),
    };
  }

  return { ...breakdown, classifications: grouped };
}
